// JWT Token handling - ported from the existing jwt-client.js

const JWT_STORAGE_KEY = 'JWT_TOKEN'
const TOKEN_PREFIX = 'Bearer '

export interface JwtClaim {
  exp?: number
  nbf?: number
  iss?: string
  aud?: string
  sub?: string
  [key: string]: unknown
}

export interface JwtToken {
  header: Record<string, unknown>
  claim: JwtClaim
  signature: string
}

function encode64(value: string): string {
  const encoded = btoa(unescape(encodeURIComponent(value)))
  return encoded.replace(/=+$/, '')
}

function decode64(value: string): string {
  // Handle URL-safe base64
  let normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  // Add padding if needed
  while (normalized.length % 4) {
    normalized += '='
  }
  return decodeURIComponent(escape(atob(normalized)))
}

export function writeToken(token: JwtToken): string {
  return TOKEN_PREFIX +
    encode64(JSON.stringify(token.header)) + '.' +
    encode64(JSON.stringify(token.claim)) + '.' +
    (token.signature || '')
}

export function readToken(header: string): JwtToken | null {
  try {
    let tokenStr = header
    if (tokenStr.indexOf(TOKEN_PREFIX) === 0) {
      tokenStr = tokenStr.substring(TOKEN_PREFIX.length)
    }

    const parts = tokenStr.split('.')
    if (parts.length < 2) return null

    return {
      header: JSON.parse(decode64(parts[0])),
      claim: JSON.parse(decode64(parts[1])),
      signature: parts[2] || ''
    }
  } catch {
    return null
  }
}

export function validateToken(token: JwtToken | string | null): boolean {
  if (!token) return false

  const parsed = typeof token === 'string' ? readToken(token) : token
  if (!parsed || !parsed.claim) return false

  const now = Date.now() / 1000

  // Check expiration
  if (parsed.claim.exp && parsed.claim.exp <= now) {
    return false
  }

  // Check not-before
  if (parsed.claim.nbf && parsed.claim.nbf > now) {
    return false
  }

  return true
}

export function storeToken(token: JwtToken | string): void {
  const tokenStr = typeof token === 'string' ? token : writeToken(token)
  localStorage.setItem(JWT_STORAGE_KEY, tokenStr)
}

export function retrieveToken(): JwtToken | null {
  const stored = localStorage.getItem(JWT_STORAGE_KEY)
  if (!stored) return null
  return readToken(stored)
}

export function clearToken(): void {
  localStorage.removeItem(JWT_STORAGE_KEY)
}

export function getAuthHeader(): string | null {
  const token = retrieveToken()
  if (!token || !validateToken(token)) return null
  return writeToken(token)
}
